import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase, authHelpers } from '../lib/supabase';
import { User } from '@supabase/supabase-js';

type UserType = 'user' | 'institution' | 'admin' | null;

interface AuthContextType {
  user: User | null;
  userType: UserType;
  userData: any;
  isLoading: boolean;
  signIn: (email: string, password: string, type: UserType) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Admin e-postaları listesi
const ADMIN_EMAILS = ['ugurtur@hotmail.com', 'admin@tupbebekuygulama.com'];

// Kayıt işlemi bayrağı (global)
let isRegistrationInProgress = false;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userType, setUserType] = useState<UserType>(null);
  const [userData, setUserData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Oturum kontrolü ve kullanıcı verilerini yükleme
  useEffect(() => {
    checkSession();

    // Auth state değişikliklerini dinle
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔐 Auth state değişti:', event, session?.user?.email);
      
      // Kayıt işlemi sırasında event'leri yok say
      if (isRegistrationInProgress) {
        console.log('⏭️ Kayıt işlemi devam ediyor, event yok sayıldı');
        return;
      }
      
      if (event === 'SIGNED_IN' && session?.user) {
        await loadUserData(session.user);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setUserType(null);
        setUserData(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const checkSession = async () => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      
      if (currentUser) {
        await loadUserData(currentUser);
      }
    } catch (error) {
      console.error('❌ Oturum kontrolü hatası:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserData = async (authUser: User): Promise<UserType> => {
    try {
      console.log('📊 Kullanıcı verileri yükleniyor:', authUser.email);
      
      // Platform admin kontrolü (e-posta listesinden veya metadata'dan)
      const isAdmin = ADMIN_EMAILS.includes(authUser.email || '') || 
                     authUser.user_metadata?.role === 'platform_admin';
      
      if (isAdmin) {
        console.log('✅ Platform admin bulundu');
        setUser(authUser);
        setUserType('admin');
        setUserData({ 
          email: authUser.email, 
          role: 'platform_admin',
          full_name: authUser.user_metadata?.full_name || 'Platform Admin'
        });
        return 'admin';
      }

      // Kurum kontrolü - user_id ile kontrol et
      const { data: institution, error: instError } = await supabase
        .from('institutions')
        .select('*')
        .eq('user_id', authUser.id)
        .is('deleted_at', null)
        .maybeSingle();

      if (!instError && institution) {
        console.log('✅ Kurum bulundu:', institution.name);
        setUser(authUser);
        setUserType('institution');
        setUserData(institution);
        return 'institution';
      }

      // Kullanıcı profili kontrolü
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', authUser.id)
        .maybeSingle();

      if (!profileError && profile) {
        console.log('✅ Kullanıcı profili bulundu:', profile.full_name);
        setUser(authUser);
        setUserType('user');
        setUserData({
          ...profile,
          email: profile.email || authUser.email,
        });
        return 'user';
      }

      // Hiçbir veri bulunamadı
      console.log('⚠️ Kullanıcı verisi bulunamadı');
      setUser(authUser);
      setUserType(null);
      setUserData({ 
        id: authUser.id, 
        email: authUser.email,
        full_name: authUser.user_metadata?.full_name || ''
      });
      return null;

    } catch (error) {
      console.error('❌ Kullanıcı verileri yükleme hatası:', error);
      setUser(authUser);
      setUserType(null);
      setUserData(null);
      return null;
    }
  };

  const signIn = async (
    email: string,
    password: string,
    loginType: UserType = 'user'
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('🔐 Giriş denemesi:', { email, loginType });

      // Admin girişi - direkt edge function'a gönder
      if (loginType === 'admin') {
        console.log('🔍 Admin girişi yapılıyor...');

        try {
          const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
          const functionUrl = `${supabaseUrl}/functions/v1/admin-login`;
          
          console.log('📡 Admin login edge function çağrılıyor:', functionUrl);
          
          const response = await fetch(functionUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
          });

          console.log('📡 Edge function yanıt durumu:', response.status);
          
          const result = await response.json();
          console.log('📡 Edge function sonucu:', result);

          if (!response.ok || !result.success) {
            console.log('❌ Admin girişi başarısız:', result.error);
            return { 
              success: false, 
              error: result.error || 'E-posta veya şifre hatalı.' 
            };
          }

          console.log('✅ Admin girişi başarılı');

          // Gerçek Supabase Auth oturumu varsa kullan
          if (result.session?.access_token && result.session?.refresh_token) {
            const { error: sessionError } = await supabase.auth.setSession({
              access_token: result.session.access_token,
              refresh_token: result.session.refresh_token,
            });
            if (sessionError) {
              console.warn('⚠️ Session set edilemedi:', sessionError.message);
            } else {
              console.log('✅ Gerçek Supabase Auth oturumu açıldı');
            }
          }
          
          const adminUser = {
            id: result.admin.id,
            email: result.admin.email,
            user_metadata: { role: 'platform_admin', full_name: result.admin.full_name }
          } as User;
          
          setUser(adminUser);
          setUserType('admin');
          setUserData({ 
            id: result.admin.id,
            email: result.admin.email, 
            role: 'platform_admin',
            full_name: result.admin.full_name || 'Platform Admin'
          });

          setTimeout(() => {
            console.log('🔄 Admin paneline yönlendiriliyor...');
            if (window.REACT_APP_NAVIGATE) {
              window.REACT_APP_NAVIGATE('/yonetici-paneli');
            } else {
              window.location.href = '/yonetici-paneli';
            }
          }, 200);
          
          return { success: true };
        } catch (fetchError) {
          console.error('❌ Admin login edge function hatası:', fetchError);
          return { 
            success: false, 
            error: 'Admin giriş servisi şu anda kullanılamıyor. Lütfen tekrar deneyin.' 
          };
        }
      }

      // Kurum ve kullanıcı girişi: önce Supabase Auth ile doğrula
      const { data, error } = await authHelpers.signIn(email, password);

      if (error) {
        console.error('❌ Giriş hatası:', error.message);
        
        if (error.message.includes('Invalid login credentials')) {
          return { 
            success: false, 
            error: 'E-posta veya şifre hatalı.' 
          };
        } else if (error.message.includes('Email not confirmed')) {
          return { success: false, error: 'E-posta adresiniz doğrulanmamış.' };
        } else if (error.message.includes('Too many requests')) {
          return { success: false, error: 'Çok fazla deneme. Lütfen birkaç dakika bekleyin.' };
        }
        
        return { success: false, error: error.message };
      }

      if (!data.user) {
        return { success: false, error: 'Giriş başarısız oldu.' };
      }

      const authUser = data.user;
      console.log('✅ Supabase Auth girişi başarılı, UUID:', authUser.id);

      // Kurum girişi: UUID ile kontrol
      if (loginType === 'institution') {
        console.log('🏥 Kurum kaydı UUID ile kontrol ediliyor...');

        const { data: institutionData, error: instCheckError } = await supabase
          .from('institutions')
          .select('*')
          .eq('user_id', authUser.id)
          .is('deleted_at', null)
          .maybeSingle();

        if (instCheckError) {
          console.error('❌ Kurum kontrol hatası:', instCheckError);
          await authHelpers.signOut();
          return { 
            success: false, 
            error: 'Kurum bilgileri kontrol edilemedi. Lütfen tekrar deneyin.' 
          };
        }

        if (!institutionData) {
          console.log('❌ Bu Auth hesabına bağlı kurum kaydı bulunamadı');
          await authHelpers.signOut();

          // Silinmiş kurum kontrolü
          const { data: deletedInst } = await supabase
            .from('institutions')
            .select('id')
            .eq('user_id', authUser.id)
            .not('deleted_at', 'is', null)
            .maybeSingle();

          if (deletedInst) {
            return { 
              success: false, 
              error: 'Bu kurum hesabı silinmiş. Lütfen yeni bir hesap oluşturun.' 
            };
          }

          return { 
            success: false, 
            error: 'Bu hesaba bağlı kurum kaydı bulunamadı. Lütfen kurum kaydı oluşturun.' 
          };
        }

        if (institutionData.approval_status === 'rejected') {
          await authHelpers.signOut();
          const reason = institutionData.rejection_reason 
            ? `\n\nRed sebebi: ${institutionData.rejection_reason}` 
            : '';
          return { 
            success: false, 
            error: `Bu kurum başvurusu reddedilmiş.${reason}\n\nDetaylı bilgi için yönetici ile iletişime geçin.`
          };
        }

        console.log('✅ Kurum kaydı bulundu:', institutionData.name, '| Durum:', institutionData.status);
      }

      // Kullanıcı girişi: UUID ile kontrol
      if (loginType === 'user') {
        console.log('👤 Kullanıcı kaydı UUID ile kontrol ediliyor...');

        const { data: userProfileData, error: profileCheckError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', authUser.id)
          .maybeSingle();

        if (profileCheckError) {
          console.error('❌ Kullanıcı profil kontrol hatası:', profileCheckError);
          await authHelpers.signOut();
          return { 
            success: false, 
            error: 'Kullanıcı bilgileri kontrol edilemedi. Lütfen tekrar deneyin.' 
          };
        }

        if (!userProfileData) {
          console.log('❌ Bu Auth hesabına bağlı kullanıcı profili bulunamadı');
          await authHelpers.signOut();
          return { 
            success: false, 
            error: 'Bu hesaba bağlı kullanıcı profili bulunamadı. Lütfen önce kayıt olun.' 
          };
        }

        if (userProfileData.status === 'suspended' || userProfileData.status === 'deleted') {
          await authHelpers.signOut();
          return { 
            success: false, 
            error: 'Bu hesap askıya alınmış veya silinmiş. Lütfen yönetici ile iletişime geçin.' 
          };
        }

        console.log('✅ Kullanıcı profili bulundu:', userProfileData.full_name);
      }

      // Kullanıcı verilerini yükle
      const loadedUserType = await loadUserData(authUser);
      
      if (!loadedUserType) {
        await authHelpers.signOut();
        return { 
          success: false, 
          error: 'Kullanıcı bilgileri yüklenemedi. Lütfen tekrar deneyin.' 
        };
      }

      console.log('✅ Kullanıcı verileri yüklendi, yönlendiriliyor...');

      setTimeout(() => {
        if (loadedUserType === 'admin') {
          window.REACT_APP_NAVIGATE('/yonetici-paneli');
        } else if (loadedUserType === 'institution') {
          window.REACT_APP_NAVIGATE('/kurum-paneli');
        } else {
          window.REACT_APP_NAVIGATE('/kullanici-paneli');
        }
      }, 200);

      return { success: true };
    } catch (error) {
      console.error('❌ Beklenmeyen giriş hatası:', error);
      return { 
        success: false, 
        error: 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.' 
      };
    }
  };

  const signOut = async () => {
    try {
      console.log('🚪 Çıkış yapılıyor...');
      await authHelpers.signOut();
      setUser(null);
      setUserType(null);
      setUserData(null);
      console.log('✅ Çıkış başarılı');
    } catch (error) {
      console.error('❌ Çıkış hatası:', error);
    }
  };

  const refreshUserData = async () => {
    if (user) {
      await loadUserData(user);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userType,
        userData,
        isLoading,
        signIn,
        signOut,
        refreshUserData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Kayıt işlemi kontrolü için export edilen fonksiyonlar
export function setRegistrationInProgress(value: boolean) {
  isRegistrationInProgress = value;
  console.log('🔄 Kayıt işlemi bayrağı:', value);
}
